import React from "react"
import { useQuery } from "@apollo/client"
import GET_WORK_BY_ID from "../../Queries/getWorkById"
import { Container, Grid, Typography, Chip } from "@mui/material"
import { motion } from "framer-motion"
import { HeroScaleAnimation } from "../../Animation/AnimationOne"
import CloseIcon from "@mui/icons-material/Close"
import WorkDetailImage from "./WorkDetailComponents/WorkDetailImage"
import { getBackgroundColors } from "./WorkDetailComponents/colorUtils"
import theme from "../../theme"
import type { WorkItem, ServiceItem, WorkDetailProps } from "./types"
import {
  MainWrapper,
  ProfileImg,
  HeadWrapper,
  TitleWrapper,
  HeroWrapper,
  IntroWrapper,
  FooterWrapper,
  ProfileTitleWrapperFooter,
  CloseDialog,
} from "./WorkDetailComponents/styles"

const getFilenameFromUrl = (url: string | URL) => {
  const path = new URL(url).pathname
  const filename = path.substring(path.lastIndexOf("/") + 1)
  return filename
}

const WorkDetail: React.FC<WorkDetailProps> = ({
  id,
  onClose,
  // containerRef,
}) => {
  const { data, loading, error } = useQuery(GET_WORK_BY_ID, {
    variables: { id },
  })

  const work = data?.workCollection?.items?.[0] as WorkItem | undefined

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error loading work detail.</div>
  if (!work) return <div>No work found.</div>

  const tertiaryColor = work.tertiaryColor ?? "transparent"
  const introTextColor = work.introTextColor ?? "transparent"

  const isVideoURL = (url?: string) =>
    [".mp4", ".webm"].some((ext) => url?.endsWith(ext))

  const heroMediaIsVideo = isVideoURL(work.heroMedia?.url)

  const workDetailBackgroundColors = getBackgroundColors(work as any)

  return (
    // <div ref={containerRef}>
    <Container maxWidth={"xl"} disableGutters sx={{ position: "relative" }}>
      <HeadWrapper>
        <TitleWrapper>
          <ProfileImg
            sx={{ backgroundColor: work.primaryColor }}
            variants={HeroScaleAnimation(0.3)}
            initial="heroinitial"
            animate="heroanimate"
            background={work.profileImg?.url}
          />
          <motion.div
            variants={HeroScaleAnimation(0.5)}
            initial="heroinitial"
            animate="heroanimate"
          >
            <Typography
              variant="h5"
              fontWeight={500}
              sx={{
                color: {
                  xs: theme.palette.text.primary,
                  sm: theme.palette.text.secondary,
                },
              }}
            >
              {work.title}
            </Typography>
          </motion.div>
        </TitleWrapper>
        <CloseDialog onClick={onClose}>
          <CloseIcon />
        </CloseDialog>
      </HeadWrapper>

      <HeroWrapper>
        {heroMediaIsVideo ? (
          <motion.video
            src={work.heroMedia?.url}
            aria-label={work.title}
            loop
            muted
            autoPlay
            playsInline
            variants={HeroScaleAnimation(0.7)}
            initial="heroinitial"
            animate="heroanimate"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <motion.img
            src={work.heroMedia?.url}
            alt={work.title}
            variants={HeroScaleAnimation(0.7)}
            initial="heroinitial"
            animate="heroanimate"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
      </HeroWrapper>

      <IntroWrapper style={{ backgroundColor: tertiaryColor }}>
        <Typography
          variant="h6"
          fontWeight={"400"}
          textAlign={"left"}
          sx={{ color: introTextColor }}
        >
          {work.description}
        </Typography>
      </IntroWrapper>

      <Grid
        container
        rowSpacing={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MainWrapper
          variants={HeroScaleAnimation(1)}
          initial="heroinitial"
          animate="heroanimate"
        >
          {work.imgOne && (
            <WorkDetailImage
              imageUrl={work.imgOne.url}
              altText="imgOne"
              title={work.imgOne.title}
              description={work.imgOne.description}
              hasPadding={getFilenameFromUrl(work.imgOne.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgOneColorRef"]}
              hasBottomBorder={work.imgOneBorderBottom ?? false}
            />
          )}

          {work.imgTwo && (
            <WorkDetailImage
              imageUrl={work.imgTwo.url}
              altText="imgTwo"
              title={work.imgTwo.title}
              description={work.imgTwo.description}
              hasPadding={getFilenameFromUrl(work.imgTwo.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgTwoColorRef"]}
              hasBottomBorder={work.imgTwoBorderBottom ?? false}
            />
          )}

          {work.imgThree && (
            <WorkDetailImage
              imageUrl={work.imgThree.url}
              altText="imgThree"
              title={work.imgThree.title}
              description={work.imgThree.description}
              hasPadding={getFilenameFromUrl(work.imgThree.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgThreeColorRef"]}
              hasBottomBorder={work.imgThreeBorderBottom ?? false}
            />
          )}

          {work.imgFour && (
            <WorkDetailImage
              imageUrl={work.imgFour.url}
              altText="imgFour"
              title={work.imgFour.title}
              description={work.imgFour.description}
              hasPadding={getFilenameFromUrl(work.imgFour.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgFourColorRef"]}
              hasBottomBorder={work.imgFourBorderBottom ?? false}
            />
          )}

          {work.imgFive && (
            <WorkDetailImage
              imageUrl={work.imgFive.url}
              altText="imgFive"
              title={work.imgFive.title}
              description={work.imgFive.description}
              hasPadding={getFilenameFromUrl(work.imgFive.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgFiveColorRef"]}
              hasBottomBorder={work.imgFiveBorderBottom ?? false}
            />
          )}

          {work.imgSix && (
            <WorkDetailImage
              imageUrl={work.imgSix.url}
              altText="imgSix"
              title={work.imgSix.title}
              description={work.imgSix.description}
              hasPadding={getFilenameFromUrl(work.imgSix.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgSixColorRef"]}
              hasBottomBorder={work.imgSixBorderBottom ?? false}
            />
          )}

          {work.imgSeven && (
            <WorkDetailImage
              imageUrl={work.imgSeven.url}
              altText="imgSeven"
              title={work.imgSeven.title}
              description={work.imgSeven.description}
              hasPadding={getFilenameFromUrl(work.imgSeven.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgSevenColorRef"]}
              hasBottomBorder={work.imgSevenBorderBottom ?? false}
            />
          )}

          {work.imgEight && (
            <WorkDetailImage
              imageUrl={work.imgEight.url}
              altText="imgEight"
              title={work.imgEight.title}
              description={work.imgEight.description}
              hasPadding={getFilenameFromUrl(work.imgEight.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgEightColorRef"]}
              hasBottomBorder={work.imgEightBorderBottom ?? false}
            />
          )}

          {work.imgNine && (
            <WorkDetailImage
              imageUrl={work.imgNine.url}
              altText="imgNine"
              title={work.imgNine.title}
              description={work.imgNine.description}
              hasPadding={getFilenameFromUrl(work.imgNine.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgNineColorRef"]}
              hasBottomBorder={work.imgNineBorderBottom ?? false}
            />
          )}

          {work.imgTen && (
            <WorkDetailImage
              imageUrl={work.imgTen.url}
              altText="imgTen"
              title={work.imgTen.title}
              description={work.imgTen.description}
              hasPadding={getFilenameFromUrl(work.imgTen.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgTenColorRef"]}
              hasBottomBorder={work.imgTenBorderBottom ?? false}
            />
          )}

          {work.imgEleven && (
            <WorkDetailImage
              imageUrl={work.imgEleven.url}
              altText="imgEleven"
              title={work.imgEleven.title}
              description={work.imgEleven.description}
              hasPadding={getFilenameFromUrl(work.imgEleven.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgElevenColorRef"]}
              hasBottomBorder={work.imgElevenBorderBottom ?? false}
            />
          )}

          {work.imgTwelve && (
            <WorkDetailImage
              imageUrl={work.imgTwelve.url}
              altText="imgTwelve"
              title={work.imgTwelve.title}
              description={work.imgTwelve.description}
              hasPadding={getFilenameFromUrl(work.imgTwelve.url).includes(
                "screen",
              )}
              backgroundColor={workDetailBackgroundColors["imgTwelveColorRef"]}
              hasBottomBorder={work.imgTwelveBorderBottom ?? false}
            />
          )}
        </MainWrapper>
      </Grid>

      <FooterWrapper container columnSpacing={8}>
        <Grid item xs={12} md={12}>
          <ProfileTitleWrapperFooter>
            <ProfileImg
              sx={{ backgroundColor: work.primaryColor }}
              variants={HeroScaleAnimation(0.3)}
              initial="heroinitial"
              animate="heroanimate"
              background={work.profileImg?.url}
            />
            <Typography
              variant="h5"
              fontWeight={"500"}
              textAlign={"center"}
              sx={{ color: theme.palette.text.primary }}
            >
              {work.title}
            </Typography>
          </ProfileTitleWrapperFooter>
        </Grid>

        <Grid item xs={12} sm={12} md={10} lg={6} xl={6}>
          <Typography
            variant="body1"
            fontWeight={"400"}
            textAlign={"left"}
            sx={{ color: theme.palette.text.primary }}
            mb={3}
          >
            {work.description}
          </Typography>

          {work.roleOne && (
            <Typography
              variant="body1"
              fontWeight={"400"}
              textAlign={"left"}
              mb={3}
            >
              {work.roleOne}
            </Typography>
          )}

          {work.roleTwo && (
            <Typography
              variant="body1"
              fontWeight={"400"}
              textAlign={"left"}
              sx={{ color: theme.palette.text.primary }}
              mb={6}
            >
              {work.roleTwo}
            </Typography>
          )}

          {work.serviceWorkCollection.items.map((service: ServiceItem) => (
            <Chip
              key={service.sys.id}
              label={service.serviceList}
              variant="outlined"
              sx={{
                marginRight: theme.spacing(1),
                marginBottom: theme.spacing(1.5),
              }}
            />
          ))}
        </Grid>

        <Grid item xs={12} md={12}>
          <Typography
            variant="body1"
            onClick={onClose}
            sx={{
              cursor: "pointer",
              color: theme.palette.text.primary,
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            }}
          >
            Close
          </Typography>
        </Grid>
      </FooterWrapper>
    </Container>
    // </div>
  )
}

export default WorkDetail
